import type { ReactNode } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import AnimatedPagination from '@/components/ui/pagination';
import { cn } from '@/lib/utils';

export interface Column<T> {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  className?: string;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  /** Marks the row a side panel is currently showing. */
  isRowActive?: (row: T) => boolean;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  isLoading?: boolean;
  emptyMessage?: string;
  className?: string;
}

/**
 * Plain read-only table for the admin lists. `ClickableTable` carries selection,
 * inline edit and bulk delete that none of these views need.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  isRowActive,
  page,
  totalPages,
  onPageChange,
  isLoading = false,
  emptyMessage = 'Nothing to show.',
  className,
}: DataTableProps<T>) {
  return (
    <div className={cn('flex min-h-0 flex-col gap-3', className)}>
      <div className="min-w-0 overflow-x-auto rounded-xl border border-content-highlight/35">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((column) => (
                <TableHead key={column.key} className={column.className}>
                  {column.header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="py-10 text-center text-sm text-white/50">
                  {isLoading ? 'Loading…' : emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow
                  key={rowKey(row)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={cn(onRowClick && 'cursor-pointer', isRowActive?.(row) && 'bg-primary/10')}
                >
                  {columns.map((column) => (
                    <TableCell key={column.key} className={column.className}>
                      {column.render(row)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 ? (
        <AnimatedPagination actualPage={page} setPage={onPageChange} totalPages={totalPages} />
      ) : null}
    </div>
  );
}
